import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import type { DeskGlanceSalesToday } from "@/shared/api/desk";
import { HomeWidget } from "./HomeWidget";
import {
  formatGlanceMoney,
  sparklinePoints,
  vsYesterdayLabel,
} from "./owner-glance-math";

/** Always-visible Sales last-7 hero graph — plan/50 wide widget. */
export function SalesTrendWidget(props: {
  companySlug: string;
  currencyCode: string;
  salesToday: DeskGlanceSalesToday | null | undefined;
  className?: string;
}) {
  const { companySlug, currencyCode, salesToday, className } = props;
  if (!salesToday?.last7 || salesToday.last7.length !== 7) return null;

  const today =
    formatGlanceMoney(salesToday.amount, currencyCode, {
      maxFractionDigits: 0,
      zeroOk: true,
    }) ?? salesToday.amount;
  const vs = vsYesterdayLabel(
    salesToday.amount,
    salesToday.yesterdayAmount,
    currencyCode,
  );

  return (
    <HomeWidget
      title="Sales trend"
      description="Last 7 days · tax-incl POS + delivered SO − returns"
      size="wide"
      className={cn(className)}
      actions={
        <Link
          to={`/c/${companySlug}/pos/tickets`}
          className="shrink-0 text-xs font-medium text-[var(--color-primary)] no-underline hover:underline"
        >
          POS sales
        </Link>
      }
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <div className="font-[family-name:var(--font-display)] text-2xl font-semibold tabular-nums tracking-tight text-[var(--color-foreground)]">
            {today}
          </div>
          <p className="mt-1 m-0 text-xs text-[var(--color-muted)]">{vs}</p>
        </div>
        <Link
          to={`/c/${companySlug}/pos/tickets`}
          className="block no-underline"
          aria-label="Sales last 7 days"
        >
          <svg
            viewBox="0 0 84 28"
            width="100%"
            height={56}
            className="max-w-[14rem] overflow-visible text-[var(--color-primary)]"
            preserveAspectRatio="none"
          >
            <polyline
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={sparklinePoints(salesToday.last7)}
            />
          </svg>
        </Link>
      </div>
    </HomeWidget>
  );
}
